// LUMINA · pages/vajra/admin/EventSection.jsx
// Phase 107: Series Editor — Series + Events 同窗口编辑

import React, { useState } from 'react';
import { API, GD, DG, CR, PANEL } from '../constants';
import ImageUpload from '../../../components/ImageUpload';
import { VajraInput, VajraBtn } from '../primitives';

const BLANK_EV_FORM = {title_zh:"",title_en:"",teacher_zh:"",teacher_en:"",location:"",subtitle_zh:"",subtitle_en:"",price_info_zh:"",price_info_en:"",sessions:[],event_type:"teaching",event_category:"normal",is_public:false,registration_required:false,capacity:"",start_date:"",end_date:"",online_link:"",spiritual_function:"",element_affinity:"",flyer_url:"",flyer_zh:"",flyer_en:"",recurring_rule:"",spiritual_grant:"",donation_type:"free",fee_amount:"",teacher_id:null,teaching_id:null,temple_id:null,start_time:"",end_time:"",timezone:"America/Toronto",intro_short:"",intro_paragraphs:"",requirements:"",fee_note:"",cover_image_url:"",is_external:false,is_featured:false,topic_hub_slug:"",gallery_images:"",energy_direction:"",description_zh:"",description_en:""};
const BLANK_SERIES_FORM = {title_zh:"",title_en:"",teacher_zh:"",teacher_en:"",description:"",description_zh:"",description_en:"",start_date:"",end_date:"",flyer_url:"",website:"",contact:"",slug:"",is_public:false,is_featured:false,theme_color:"indigo-gold"};

export default function EventSection({ token, headers, eventList, setEventList, teacherList, teachingList, templeList = [], hubList = [] }) {
  // ── Existing state ────────────────────────────────────────────────────────
  const [evForm, setEvForm] = useState(BLANK_EV_FORM);
  const [editingEventId, setEditingEventId] = useState(null);
  const [evSession, setEvSession] = useState({date:"",time:"",title_zh:"",price:0,free:false});
  const [editSessionIdx, setEditSessionIdx] = useState(null);
  const [flyerUrl, setFlyerUrl] = useState("");
  const [flyerFile, setFlyerFile] = useState(null);
  const [flyerFileName, setFlyerFileName] = useState("");
  const [flyerLoading, setFlyerLoading] = useState(false);
  const [flyerResult, setFlyerResult] = useState(null);
  const [showFlyerCard, setShowFlyerCard] = useState(false);
  const [flyerError, setFlyerError] = useState("");
  const [flyerDragOver, setFlyerDragOver] = useState(false);
  const [msg, setMsg] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [catFilter, setCatFilter] = useState("");

  // ── Series state ──────────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState('list');
  const [seriesList, setSeriesList] = useState([]);
  const [activeSeries, setActiveSeries] = useState(null);
  const [seriesForm, setSeriesForm] = useState(BLANK_SERIES_FORM);
  const [seriesEvents, setSeriesEvents] = useState([]);
  const [editingSeriesEvId, setEditingSeriesEvId] = useState(null);
  const [seriesEvForm, setSeriesEvForm] = useState(BLANK_EV_FORM);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [seriesMsg, setSeriesMsg] = useState("");

  // ── Registration modal state ──────────────────────────────────────────────
  const [regModal, setRegModal] = useState(null);  // {event_id, title} or null
  const [regList, setRegList] = useState([]);
  const [regLoading, setRegLoading] = useState(false);
  const [regMsg, setRegMsg] = useState("");
  const [financeData, setFinanceData] = useState(null);

  const loadRegs = async (eventId) => {
    setRegLoading(true); setRegMsg(""); setFinanceData(null);
    const [regs, finance] = await Promise.all([
      fetch(`${API}/vajra/staff/registrations?event_id=${eventId}`, {headers}).then(r=>r.ok?r.json():[]).catch(()=>[]),
      fetch(`${API}/vajra/staff/events/${eventId}/finance`, {headers}).then(r=>r.ok?r.json():null).catch(()=>null),
    ]);
    setRegList(Array.isArray(regs) ? regs : []);
    setFinanceData(finance);
    setRegLoading(false);
  };

  const markPayment = async (regId, status) => {
    const r = await fetch(`${API}/vajra/staff/registrations/${regId}/payment`, {
      method: "PUT", headers,
      body: JSON.stringify({payment_status: status}),
    });
    if (r.ok) { setRegMsg("✓ 已更新"); loadRegs(regModal.event_id); }
    else { setRegMsg("✗ 更新失败"); }
  };

  // ── Flyer helpers ─────────────────────────────────────────────────────────
  const processFile = (file) => {
    if (!file || !file.type.startsWith("image/")) { setFlyerError("请选择图片文件（jpg/png/webp）"); return; }
    if (file.size > 20 * 1024 * 1024) { setFlyerError("文件过大（最大20MB）"); return; }
    setFlyerError(""); setFlyerFileName(file.name); setFlyerFile(file);
  };
  const handleDrop = (e) => { e.preventDefault(); setFlyerDragOver(false); processFile(e.dataTransfer?.files?.[0]); };
  const handleFileInput = (e) => processFile(e.target?.files?.[0]);

  const submitFlyer = async (e) => {
    e.preventDefault();
    if (!flyerFile && !flyerUrl.trim()) return;
    setFlyerLoading(true); setFlyerError(""); setFlyerResult(null);
    try {
      let r;
      if (flyerFile) {
        const fd = new FormData();
        fd.append("file", flyerFile);
        if (flyerUrl.trim()) fd.append("flyer_url", flyerUrl.trim());
        r = await fetch(`https://api.lumarcai.com/vajra/staff/events/from-flyer`, {
          method: "POST", headers: { Authorization: headers.Authorization }, body: fd,
        });
      } else {
        r = await fetch(`https://api.lumarcai.com/vajra/staff/events/from-flyer`, {
          method: "POST", headers, body: JSON.stringify({ flyer_url: flyerUrl.trim() }),
        });
      }
      const d = await r.json();
      if (r.ok) {
        if (d.series && Array.isArray(d.events)) {
          // Series+Events shape — switch to Series Editor
          setFlyerResult({ _isSeries: true, series: d.series, events: d.events });
          setActiveSeries(d.series);
          setSeriesForm({
            title_zh: d.series.title_zh || "",
            title_en: d.series.title_en || "",
            teacher_zh: d.series.teacher_zh || "",
            teacher_en: d.series.teacher_en || "",
            description: d.series.description || "",
            start_date: d.series.start_date || "",
            end_date: d.series.end_date || "",
            flyer_url: d.series.flyer_url || "",
            website: d.series.website || "",
            contact: d.series.contact || "",
            slug: d.series.slug || "",
            is_public: d.series.is_public || false, is_featured: false,
          });
          setSeriesEvents(d.events);
          setEditingSeriesEvId(null);
          setViewMode('series');
          setMsg(`已创建系列「${d.series.title_zh}」及 ${d.events.length} 个场次，请逐一审阅后保存。`);
          setEventList(prev => [...d.events.filter(ev => !ev.series_id), ...prev]);
        } else {
          // Legacy single-event shape
          setFlyerResult(d);
          setEvForm({
            ...BLANK_EV_FORM,
            title_zh: d.title_zh || "", title_en: d.title_en || "",
            teacher_zh: d.teacher_zh || "", teacher_en: d.teacher_en || "",
            location: d.location || "", event_type: d.event_type || "teaching",
            start_date: d.start_date || "", end_date: d.end_date || d.start_date || "",
            start_time: d.start_time || "", end_time: d.end_time || "",
            donation_type: d.donation_type || "free",
            fee_amount: d.fee_amount ? String(d.fee_amount / 100) : "",
            registration_required: d.registration_required || false,
            flyer_url: d.flyer_url || "",
            sessions: Array.isArray(d.sessions) ? d.sessions : (typeof d.sessions === 'string' ? (()=>{try{return JSON.parse(d.sessions)}catch{return []}})() : []),
            is_public: d.is_public || false, is_featured: d.is_featured || false, slug: d.slug || "",
          });
          setEditingEventId(d.id);
          setMsg(`已创建草稿「${d.title_zh}」，表单已自动填充。`);
          setEventList(prev => [d, ...prev]);
          setTimeout(() => document.getElementById("ev-form-anchor")?.scrollIntoView({ behavior: "smooth" }), 100);
        }
        setShowFlyerCard(false); setFlyerUrl(""); setFlyerFile(null); setFlyerFileName("");
      } else {
        setFlyerError(d.detail || "分析失败");
      }
    } catch (err) {
      setFlyerError("网络错误，请重试" + (err.message ? " (" + err.message + ")" : ""));
    }
    setFlyerLoading(false);
  };

  // ── Event form helpers ────────────────────────────────────────────────────
  const saveSession = () => {
    if (editSessionIdx !== null) {
      setEvForm(f => { const u = [...f.sessions]; u[editSessionIdx] = {...evSession}; return {...f, sessions: u}; });
      setEditSessionIdx(null);
    } else {
      setEvForm(f => ({...f, sessions: [...f.sessions, {...evSession}]}));
    }
    setEvSession({date:"",time:"",title_zh:"",price:0,free:false});
  };
  const startEditSession = (idx) => { setEvSession({...evForm.sessions[idx]}); setEditSessionIdx(idx); };

  const submitEvent = async (e) => {
    e.preventDefault();
    const payload = {...evForm,
      capacity: evForm.capacity ? parseInt(evForm.capacity) : null,
      flyer_url: evForm.flyer_url || null, flyer_zh: evForm.flyer_zh || null, flyer_en: evForm.flyer_en || null,
      recurring_rule: evForm.recurring_rule || null, spiritual_grant: evForm.spiritual_grant || null,
      donation_type: evForm.donation_type || "free",
      fee_amount: evForm.fee_amount ? Math.round(parseFloat(evForm.fee_amount) * 100) : null,
      temple_id: evForm.temple_id || null, requirements: evForm.requirements || null,
      fee_note: evForm.fee_note || null, cover_image_url: evForm.cover_image_url || null,
      topic_hub_slug: evForm.topic_hub_slug || null,
      gallery_images: evForm.gallery_images ? evForm.gallery_images.split("\n").map(u=>u.trim()).filter(Boolean) : null,
      description_zh: evForm.description_zh || null,
      description_en: evForm.description_en || null,
      energy_direction: evForm.energy_direction || null,
    };
    const url = editingEventId ? `${API}/vajra/staff/events/${editingEventId}` : `${API}/vajra/staff/events`;
    const method = editingEventId ? "PUT" : "POST";
    const r = await fetch(url, {method, headers, body: JSON.stringify(payload)});
    if (r.ok) {
      setMsg(editingEventId ? "✓ 活动已更新" : "✓ 法会活动已创建");
      setEvForm(BLANK_EV_FORM); setEditingEventId(null);
      const evs = await fetch(`${API}/vajra/staff/events?standalone=true`, {headers}).then(r=>r.ok?r.json():[]).catch(()=>[]);
      setEventList(Array.isArray(evs)?evs:evs.events||[]);
    } else {
      const d = await r.json(); setMsg("✗ " + (d.detail||"操作失败"));
    }
  };

  const deleteEvent = async (evId) => {
    if (!window.confirm("确认停用此活动？")) return;
    const r = await fetch(`${API}/vajra/staff/events/${evId}`, {method:"DELETE", headers});
    if (r.ok) { setMsg("✓ 活动已停用"); setEventList(prev => prev.map(e => e.id===evId ? {...e,is_active:false} : e)); }
    else setMsg("✗ 删除失败");
  };
  const permanentDeleteEvent = async (evId) => {
    if (!window.confirm("⚠️ 确认永久删除？此操作不可撤销，将从数据库彻底移除。")) return;
    const r = await fetch(`${API}/vajra/staff/events/${evId}?permanent=true`, {method:"DELETE", headers});
    if (r.ok) { setMsg("✓ 活动已永久删除"); setEventList(prev => prev.filter(e => e.id!==evId)); }
    else setMsg("✗ 删除失败");
  };
  const reactivateEvent = async (evId) => {
    const r = await fetch(`${API}/vajra/staff/events/${evId}`, {method:"PUT", headers, body: JSON.stringify({is_active:true})});
    if (r.ok) { setMsg("✓ 活动已恢复"); setEventList(prev => prev.map(e => e.id===evId ? {...e,is_active:true} : e)); }
    else setMsg("✗ 恢复失败");
  };
  const startEditEvent = (ev) => {
    let s = ev.sessions;
    if (typeof s === 'string') { try { s = JSON.parse(s); } catch(e) { s = []; } }
    if (!Array.isArray(s)) s = [];
    setEvForm({
      title_zh:ev.title_zh||"",title_en:ev.title_en||"",teacher_zh:ev.teacher_zh||"",teacher_en:ev.teacher_en||"",
      location:ev.location||"",subtitle_zh:ev.subtitle_zh||"",subtitle_en:ev.subtitle_en||"",
      price_info_zh:ev.price_info_zh||"",price_info_en:ev.price_info_en||"",sessions:s,
      event_type:ev.event_type||"teaching",event_category:ev.event_category||"normal",is_public:ev.is_public||false,
      registration_required:ev.registration_required||false,capacity:ev.capacity||"",
      start_date:ev.start_date||"",end_date:ev.end_date||"",online_link:ev.online_link||"",
      spiritual_function:ev.spiritual_function||"",element_affinity:ev.element_affinity||"",
      flyer_url:ev.flyer_url||"",flyer_zh:ev.flyer_zh||"",flyer_en:ev.flyer_en||"",
      recurring_rule:ev.recurring_rule||"",spiritual_grant:ev.spiritual_grant||"",
      donation_type:ev.donation_type||"free",fee_amount:ev.fee_amount?String(ev.fee_amount/100):"",
      teacher_id:ev.teacher_id||null,teaching_id:ev.teaching_id||null,temple_id:ev.temple_id||null,
      requirements:ev.requirements||"",fee_note:ev.fee_note||"",cover_image_url:ev.cover_image_url||"",
      is_external:ev.is_external||false,is_featured:ev.is_featured||false,
      topic_hub_slug:ev.topic_hub_slug||"",energy_direction:ev.energy_direction||"",
      description_zh:ev.description_zh||"",description_en:ev.description_en||"",
      gallery_images:Array.isArray(ev.gallery_images)?ev.gallery_images.join("\n"):(typeof ev.gallery_images==='string'?(()=>{try{return JSON.parse(ev.gallery_images).join("\n")}catch(e){return ev.gallery_images}})():""),
      start_time:ev.start_time||"",end_time:ev.end_time||"",timezone:ev.timezone||"America/Toronto",
    });
    setEditingEventId(ev.id);
    window.scrollTo(0,0);
  };

  // ── Series API functions ───────────────────────────────────────────────────
  const loadSeriesList = async () => {
    setSeriesLoading(true);
    try {
      const r = await fetch(`${API}/vajra/staff/series`, {headers});
      if (r.ok) {
        const data = await r.json();
        setSeriesList(Array.isArray(data) ? data : (data.series || data.items || []));
      } else setSeriesMsg("✗ 加载系列列表失败");
    } catch(e) { setSeriesMsg("✗ 网络错误"); }
    setSeriesLoading(false);
  };

  const loadSeries = async (id) => {
    setSeriesLoading(true); setSeriesMsg("");
    try {
      const r = await fetch(`${API}/vajra/staff/series/${id}?include_events=true`, {headers});
      if (r.ok) {
        const data = await r.json();
        setActiveSeries(data);
        setSeriesForm({
          title_zh: data.title_zh||"", title_en: data.title_en||"",
          teacher_zh: data.teacher_zh||"", teacher_en: data.teacher_en||"",
          description: data.description||"",
          description_zh: data.description_zh||"",
          description_en: data.description_en||"",
          start_date: data.start_date||"", end_date: data.end_date||"",
          flyer_url: data.flyer_url||"", website: data.website||"", contact: data.contact||"", slug: data.slug||"", is_public: data.is_public||false, is_featured: data.is_featured||false, theme_color: data.theme_color||"indigo-gold",
        });
        setSeriesEvents(Array.isArray(data.events) ? data.events : []);
        setEditingSeriesEvId(null);
      } else setSeriesMsg("✗ 加载系列失败");
    } catch(e) { setSeriesMsg("✗ 网络错误"); }
    setSeriesLoading(false);
  };

  const saveSeriesMetadata = async () => {
    if (!activeSeries) return;
    setSeriesMsg("");
    try {
      const r = await fetch(`${API}/vajra/staff/series/${activeSeries.id}`, {
        method:"PUT", headers, body: JSON.stringify(seriesForm),
      });
      if (r.ok) {
        const updated = await r.json();
        setActiveSeries(prev => ({...prev, ...updated}));
        setSeriesMsg("✓ 系列信息已保存");
        setSeriesList(prev => prev.map(s => s.id===activeSeries.id ? {...s,...updated} : s));
      } else {
        const d = await r.json(); setSeriesMsg("✗ " + (d.detail||"保存失败"));
      }
    } catch(e) { setSeriesMsg("✗ 网络错误"); }
  };

  const startEditSeriesEvent = (ev) => {
    let s = ev.sessions;
    if (typeof s === 'string') { try { s = JSON.parse(s); } catch(e) { s = []; } }
    if (!Array.isArray(s)) s = [];
    setSeriesEvForm({
      ...BLANK_EV_FORM,
      title_zh:ev.title_zh||"", title_en:ev.title_en||"",
      teacher_zh:ev.teacher_zh||"", teacher_en:ev.teacher_en||"",
      location:ev.location||"", event_type:ev.event_type||"teaching",
      is_public: ev.is_public ?? false, registration_required: ev.registration_required ?? false,
      start_date:ev.start_date||"", end_date:ev.end_date||"",
      start_time:ev.start_time||"", end_time:ev.end_time||"",
      timezone:ev.timezone||"America/Toronto",
      donation_type:ev.donation_type||"free",
      fee_amount:ev.fee_amount?String(ev.fee_amount/100):"",
      flyer_url:ev.flyer_url||"",
      fee_note:ev.fee_note||"", spiritual_function:ev.spiritual_function||"",
      element_affinity:ev.element_affinity||"", online_link:ev.online_link||"",
      requirements:ev.requirements||"",
      topic_hub_slug:ev.topic_hub_slug||"", energy_direction:ev.energy_direction||"",
      description_zh:ev.description_zh||"",description_en:ev.description_en||"",
      teacher_id:ev.teacher_id||null, teaching_id:ev.teaching_id||null,
      temple_id:ev.temple_id||null, capacity:ev.capacity||"",
      sessions:s,
    });
    setEditingSeriesEvId(ev.id);
  };

  const saveSeriesEventFn = async (evId) => {
    setSeriesMsg("");
    const payload = {
      ...seriesEvForm,
      capacity: seriesEvForm.capacity ? parseInt(seriesEvForm.capacity) : null,
      fee_amount: seriesEvForm.fee_amount ? Math.round(parseFloat(seriesEvForm.fee_amount) * 100) : null,
      flyer_url: seriesEvForm.flyer_url || null,
      donation_type: seriesEvForm.donation_type || "free",
      requirements: seriesEvForm.requirements || null,
      fee_note: seriesEvForm.fee_note || null,
      topic_hub_slug: seriesEvForm.topic_hub_slug || null,
      description_zh: seriesEvForm.description_zh || null,
      description_en: seriesEvForm.description_en || null,
      energy_direction: seriesEvForm.energy_direction || null,
      temple_id: seriesEvForm.temple_id || null,
      teacher_id: seriesEvForm.teacher_id || null,
      teaching_id: seriesEvForm.teaching_id || null,
      gallery_images: seriesEvForm.gallery_images || null,
      cover_image_url: seriesEvForm.cover_image_url || null,
    };
    try {
      const r = await fetch(`${API}/vajra/staff/events/${evId}`, {method:"PUT", headers, body:JSON.stringify(payload)});
      if (r.ok) {
        const updated = await r.json();
        // Merge backend response but force is_public from form (backend may return null)
        setSeriesEvents(prev => prev.map(ev => ev.id===evId ? {...ev, ...updated, is_public: seriesEvForm.is_public} : ev));
        setEditingSeriesEvId(null);
        setSeriesMsg("✓ 场次已保存");
        setEventList(prev => prev.map(ev => ev.id===evId ? {...ev, ...updated, is_public: seriesEvForm.is_public} : ev));
      } else {
        const d = await r.json(); setSeriesMsg("✗ " + (d.detail||"保存失败"));
      }
    } catch(e) { setSeriesMsg("✗ 网络错误"); }
  };

  const deleteSeriesEventFn = async (evId) => {
    if (seriesEvents.length <= 1) {
      if (!window.confirm("⚠️ 这是系列的最后一个场次，确认删除？")) return;
    } else {
      if (!window.confirm("确认删除此场次？")) return;
    }
    try {
      const r = await fetch(`${API}/vajra/staff/events/${evId}?permanent=true`, {method:"DELETE", headers});
      if (r.ok) {
        setSeriesEvents(prev => prev.filter(ev => ev.id!==evId));
        if (editingSeriesEvId===evId) setEditingSeriesEvId(null);
        setSeriesMsg("✓ 场次已删除");
        setEventList(prev => prev.filter(ev => ev.id!==evId));
      } else setSeriesMsg("✗ 删除失败");
    } catch(e) { setSeriesMsg("✗ 网络错误"); }
  };

  const addSeriesEventFn = async () => {
    if (!activeSeries) return;
    try {
      const newEv = {
        ...BLANK_EV_FORM,
        title_zh: "新场次（待填写）",
        teacher_zh: seriesForm.teacher_zh || "",
        teacher_en: seriesForm.teacher_en || "",
        flyer_url: seriesForm.flyer_url || null,
        series_id: activeSeries.id,
      };
      const r = await fetch(`${API}/vajra/staff/events`, {method:"POST", headers, body:JSON.stringify(newEv)});
      if (r.ok) {
        const created = await r.json();
        setSeriesEvents(prev => [...prev, created]);
        setEventList(prev => created.series_id ? prev : [created, ...prev]);
        startEditSeriesEvent(created);
        setSeriesMsg("✓ 新场次已创建，请填写详情后保存");
      } else {
        const d = await r.json(); setSeriesMsg("✗ " + (d.detail||"创建失败"));
      }
    } catch(e) { setSeriesMsg("✗ 网络错误"); }
  };

  const deleteSeriesFn = async () => {
    if (!activeSeries) return;
    const count = seriesEvents.length;
    if (!window.confirm(`确认删除整个系列「${activeSeries.title_zh}」？\n将同时删除此系列下所有 ${count} 个场次，此操作不可撤销。`)) return;
    try {
      const r = await fetch(`${API}/vajra/staff/series/${activeSeries.id}?permanent=true`, {method:"DELETE", headers});
      if (r.ok) {
        const deletedIds = new Set(seriesEvents.map(ev => ev.id));
        setEventList(prev => prev.filter(ev => !deletedIds.has(ev.id)));
        setActiveSeries(null); setSeriesEvents([]); setEditingSeriesEvId(null);
        setSeriesMsg("✓ 系列及所有场次已删除");
        setSeriesList(prev => prev.filter(s => s.id!==activeSeries.id));
      } else setSeriesMsg("✗ 删除失败");
    } catch(e) { setSeriesMsg("✗ 网络错误"); }
  };

  const switchTab = (mode) => {
    setViewMode(mode);
    setMsg(""); setSeriesMsg("");
    if (mode === 'series' && !seriesList.length && !activeSeries) loadSeriesList();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{background:PANEL,borderRadius:12,padding:"22px 22px",border:`1px solid rgba(6,78,59,0.12)`}}>
      {msg && (
        <div style={{fontSize:13,padding:"8px 14px",borderRadius:8,marginBottom:16,
          background:msg.startsWith("✓")?"rgba(6,78,59,0.06)":"rgba(107,26,26,0.06)",
          color:msg.startsWith("✓")?DG:CR}}>{msg}</div>
      )}

      {/* ── Flyer Upload Card ─────────────────────────────────────────────── */}
      <div style={{marginBottom:18,border:`1px dashed ${flyerDragOver?"rgba(107,79,16,0.7)":"rgba(107,79,16,0.3)"}`,borderRadius:10,padding:16,background:flyerDragOver?"rgba(212,175,55,0.06)":"rgba(253,251,247,0.6)",transition:"all .2s"}}>
        <div onClick={()=>setShowFlyerCard(!showFlyerCard)} style={{cursor:"pointer",display:"flex",alignItems:"center",gap:8,userSelect:"none"}}>
          <span style={{fontSize:16}}>{showFlyerCard?"▾":"▸"}</span>
          <span style={{fontSize:13,color:GD,fontWeight:600,letterSpacing:.5}}>📸 从海报创建法会</span>
          <span style={{fontSize:10,color:"#999"}}>— 拖放或点击上传海报，AI 自动提取信息</span>
        </div>
        {showFlyerCard && (
          <div style={{marginTop:12}}>
            <div
              onDragOver={e=>{e.preventDefault();setFlyerDragOver(true);}}
              onDragLeave={()=>setFlyerDragOver(false)}
              onDrop={handleDrop}
              onClick={()=>document.getElementById("flyer-file-input").click()}
              style={{border:`2px dashed ${flyerFile?"rgba(6,78,59,0.3)":"rgba(107,79,16,0.25)"}`,borderRadius:10,padding:"28px 16px",textAlign:"center",cursor:"pointer",background:flyerFile?"rgba(6,78,59,0.03)":"rgba(255,255,255,0.5)",transition:"all .2s",marginBottom:12}}>
              {flyerFile ? (
                <div><span style={{fontSize:28}}>📸</span><div style={{fontSize:13,color:"#065f46",fontWeight:600,marginTop:6}}>{flyerFileName}</div><div style={{fontSize:10,color:"#999",marginTop:2}}>点击更换文件</div></div>
              ) : (
                <div><span style={{fontSize:28}}>📥</span><div style={{fontSize:13,color:GD,fontWeight:600,marginTop:6}}>拖放海报到此处</div><div style={{fontSize:10,color:"#999",marginTop:2}}>或点击选择文件 · jpg/png/webp · 最大20MB</div></div>
              )}
              <input id="flyer-file-input" type="file" accept="image/*" onChange={handleFileInput} style={{display:"none"}}/>
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <span style={{fontSize:10,color:"#aaa",whiteSpace:"nowrap"}}>或贴 URL：</span>
              <input value={flyerUrl} onChange={e=>setFlyerUrl(e.target.value)} placeholder="https://... （可选）"
                style={{flex:1,padding:"6px 10px",border:"1px solid rgba(107,79,16,0.15)",borderRadius:6,fontSize:12,outline:"none",background:"#fff"}}/>
              <button onClick={submitFlyer} disabled={flyerLoading||(!flyerFile&&!flyerUrl.trim())} style={{
                padding:"8px 18px",background:`linear-gradient(135deg,${GD},#A07828)`,border:"none",borderRadius:7,
                color:"#fff",fontSize:12,fontWeight:600,cursor:flyerLoading?"wait":"pointer",whiteSpace:"nowrap",opacity:flyerLoading?0.7:1,
              }}>{flyerLoading ? "分析中…" : "分析海报"}</button>
            </div>
            {flyerError && <div style={{marginTop:8,fontSize:12,color:"#dc2626"}}>{flyerError}</div>}
          </div>
        )}
      </div>

      {/* ── View Mode Tabs ────────────────────────────────────────────────── */}
      <div style={{display:"flex",borderBottom:`1px solid rgba(212,175,55,0.25)`,marginBottom:20}}>
        {[['list','全部活动'],['series','按系列管理']].map(([mode,label]) => (
          <button key={mode} onClick={()=>switchTab(mode)} style={{
            padding:"8px 20px",border:"none",background:"transparent",cursor:"pointer",
            borderBottom: viewMode===mode ? `2px solid ${GD}` : "2px solid transparent",
            color: viewMode===mode ? GD : "#999",
            fontSize:13,fontWeight:600,transition:"all .15s",
          }}>{label}</button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* LIST MODE — existing event form + event list                        */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {viewMode === 'list' && (
        <>
          <div id="ev-form-anchor" style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:18}}>
            <div style={{fontSize:15,color:DG}}>{editingEventId?"编辑法会活动":"创建法会活动"}</div>
            {editingEventId&&<VajraBtn small variant="outline" onClick={()=>{setEditingEventId(null);setEvForm(BLANK_EV_FORM);}}>取消编辑</VajraBtn>}
          </div>
          <form onSubmit={submitEvent}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <VajraInput label="活动名称（中文）*" value={evForm.title_zh} onChange={v=>setEvForm({...evForm,title_zh:v})} placeholder="如：2026金刚萨埵法会" required/>
              <VajraInput label="Event Name (English)" value={evForm.title_en} onChange={v=>setEvForm({...evForm,title_en:v})} placeholder="e.g. 2026 Vajrasattva Puja"/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <VajraInput label="主法师（中文）*" value={evForm.teacher_zh} onChange={v=>setEvForm({...evForm,teacher_zh:v})} placeholder="如：祖古益西仁波切" required/>
              <VajraInput label="Teacher (English)" value={evForm.teacher_en} onChange={v=>setEvForm({...evForm,teacher_en:v})} placeholder="e.g. Tulku Yeshe Rinpoche"/>
            </div>
            <VajraInput label="地点" value={evForm.location} onChange={v=>setEvForm({...evForm,location:v})} placeholder="地址 / Zoom（线上）" required/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <VajraInput label="副标题（中文·选填）" value={evForm.subtitle_zh} onChange={v=>setEvForm({...evForm,subtitle_zh:v})} placeholder="如：祖古益西仁波切主法"/>
              <VajraInput label="Subtitle (English·optional)" value={evForm.subtitle_en} onChange={v=>setEvForm({...evForm,subtitle_en:v})} placeholder="e.g. Presided by Tulku Yeshe Rinpoche"/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <VajraInput label="价格说明（中文·选填）" value={evForm.price_info_zh} onChange={v=>setEvForm({...evForm,price_info_zh:v})} placeholder="如：单课 $30 | 全程 $120"/>
              <VajraInput label="Price Info (English·optional)" value={evForm.price_info_en} onChange={v=>setEvForm({...evForm,price_info_en:v})} placeholder="e.g. Per session $30 | Full $120"/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
              <div>
                <div style={{fontSize:11,color:"#aaa",letterSpacing:1.2,textTransform:"uppercase",marginBottom:6}}>活动类型</div>
                <select value={evForm.event_type} onChange={e=>setEvForm({...evForm,event_type:e.target.value})}
                  style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)"}}>
                  <option value="weekly_practice">周共修 Weekly Practice</option>
                  <option value="teaching">开示 Teaching</option>
                  <option value="empowerment">灌顶 Empowerment</option>
                  <option value="retreat">禅修营 Retreat</option>
                  <option value="puja">法会 Puja</option>
                  <option value="ritual">法事 Ritual</option>
                  <option value="other">其他 Other</option>
                  <option value="external">外部 External</option>
                </select>
              </div>
              <div>
                <div style={{fontSize:11,color:"#aaa",letterSpacing:1.2,textTransform:"uppercase",marginBottom:6}}>名额上限</div>
                <input type="number" min="1" value={evForm.capacity} onChange={e=>setEvForm({...evForm,capacity:e.target.value})}
                  placeholder="留空=不限" style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,boxSizing:"border-box"}}/>
              </div>
            </div>
            <div style={{marginBottom:12}}>
              <label style={{fontSize:12,color:"rgba(0,0,0,0.82)",display:"block",marginBottom:4}}>活动分类 (event_category)</label>
              <select value={evForm.event_category||"normal"} onChange={e=>setEvForm({...evForm,event_category:e.target.value})} style={{padding:"8px 12px",borderRadius:8,border:"1px solid rgba(212,175,55,0.4)",background:"#fff",fontSize:13}}>
                <option value="normal">普通</option>
                <option value="important">重要法讯</option>
                <option value="feature">精选推荐</option>
                <option value="teaching">教法</option>
                <option value="group_practice">共修</option>
              </select>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
              <VajraInput label="开始日期" value={evForm.start_date} onChange={v=>setEvForm({...evForm,start_date:v})} type="date" small/>
              <VajraInput label="结束日期（选填）" value={evForm.end_date} onChange={v=>setEvForm({...evForm,end_date:v})} type="date" small/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1.4fr",gap:8,marginBottom:12}}>
              <VajraInput label="开始时间（选填）" value={evForm.start_time} onChange={v=>setEvForm({...evForm,start_time:v})} type="time" small/>
              <VajraInput label="结束时间（选填）" value={evForm.end_time} onChange={v=>setEvForm({...evForm,end_time:v})} type="time" small/>
              <div>
                <label style={{display:"block",fontSize:11,color:"#aaa",letterSpacing:1.2,textTransform:"uppercase",marginBottom:5}}>时区</label>
                <select value={evForm.timezone||"America/Toronto"} onChange={e=>setEvForm({...evForm,timezone:e.target.value})}
                  style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"#fff"}}>
                  <option value="America/Toronto">多伦多 / 纽约 (ET)</option>
                  <option value="America/Vancouver">温哥华 (PT)</option>
                  <option value="America/Chicago">芝加哥 (CT)</option>
                  <option value="Asia/Shanghai">北京 / 上海 (CST)</option>
                  <option value="Asia/Tokyo">东京 (JST)</option>
                  <option value="Asia/Kolkata">印度 (IST)</option>
                  <option value="Europe/London">伦敦 (GMT/BST)</option>
                  <option value="Europe/Paris">巴黎 (CET/CEST)</option>
                  <option value="Australia/Sydney">悉尼 (AEST)</option>
                </select>
              </div>
            </div>
            <VajraInput label="线上链接（选填）" value={evForm.online_link} onChange={v=>setEvForm({...evForm,online_link:v})} placeholder="Zoom / Meet URL"/>
            {(evForm.event_type==="puja"||evForm.event_type==="ritual"||evForm.event_type==="empowerment") && (
              <div style={{display:"flex",gap:12,marginBottom:14}}>
                <div style={{flex:1}}>
                  <label style={{display:"block",fontSize:11,color:"#aaa",letterSpacing:1.2,textTransform:"uppercase",marginBottom:5}}>法会功能（选填）</label>
                  <select value={evForm.spiritual_function} onChange={e=>setEvForm({...evForm,spiritual_function:e.target.value})}
                    style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e0d0",borderRadius:8,fontSize:13,background:"#fafafa"}}>
                    <option value="">-- 选择功能 --</option>
                    <option value="息灾">息灾（消除障碍）</option>
                    <option value="增福">增福（提升福报）</option>
                    <option value="降伏">降伏（净化负能量）</option>
                    <option value="敬爱">敬爱（和谐人际）</option>
                    <option value="长寿">长寿（延寿健康）</option>
                    <option value="净化">净化（清净业力）</option>
                  </select>
                </div>
                <div style={{flex:1}}>
                  <label style={{display:"block",fontSize:11,color:"#aaa",letterSpacing:1.2,textTransform:"uppercase",marginBottom:5}}>五行亲和（选填）</label>
                  <select value={evForm.element_affinity} onChange={e=>setEvForm({...evForm,element_affinity:e.target.value})}
                    style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e0d0",borderRadius:8,fontSize:13,background:"#fafafa"}}>
                    <option value="">-- 选择元素 --</option>
                    <option value="Water">水 Water</option>
                    <option value="Fire">火 Fire</option>
                    <option value="Wood">木 Wood</option>
                    <option value="Metal">金 Metal</option>
                    <option value="Earth">土 Earth</option>
                  </select>
                </div>
              </div>
            )}
            <div style={{display:"flex",gap:20,marginBottom:14}}>
              <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:"#555",cursor:"pointer"}}>
                <input type="checkbox" checked={evForm.is_public} onChange={e=>setEvForm({...evForm,is_public:e.target.checked})}/>
                公开显示（无需登录可见）
              </label>
              <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:"#555",cursor:"pointer"}}>
                <input type="checkbox" checked={evForm.registration_required} onChange={e=>setEvForm({...evForm,registration_required:e.target.checked})}/>
                需要报名
              </label>
            </div>
            {/* Sessions */}
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,color:"#aaa",letterSpacing:1.5,textTransform:"uppercase",marginBottom:8}}>场次安排</div>
              {evForm.sessions.map((s,i)=>(
                <div key={i} style={{fontSize:12,color:editSessionIdx===i?GD:"#666",padding:"5px 10px",background:editSessionIdx===i?"rgba(212,175,55,0.12)":"rgba(6,78,59,0.05)",borderRadius:6,marginBottom:4,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span>{s.date} {s.time} · {s.title_zh}{editSessionIdx===i&&" ← 编辑中"}</span>
                  <div style={{display:"flex",gap:4}}>
                    <button type="button" onClick={()=>startEditSession(i)} style={{background:"none",border:"none",color:GD,cursor:"pointer",fontSize:11}}>✎</button>
                    <button type="button" onClick={()=>{setEvForm(f=>({...f,sessions:f.sessions.filter((_,j)=>j!==i)}));if(editSessionIdx===i){setEditSessionIdx(null);setEvSession({date:"",time:"",title_zh:"",price:0,free:false});}}} style={{background:"none",border:"none",color:"#e57373",cursor:"pointer",fontSize:14}}>×</button>
                  </div>
                </div>
              ))}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:8}}>
                <VajraInput label="日期" value={evSession.date} onChange={v=>setEvSession({...evSession,date:v})} type="date" small/>
                <VajraInput label="时间" value={evSession.time} onChange={v=>setEvSession({...evSession,time:v})} type="time" small/>
              </div>
              <VajraInput label="场次标题" value={evSession.title_zh} onChange={v=>setEvSession({...evSession,title_zh:v})} placeholder="如：心、觉性与大圆满" small/>
              <div style={{display:"flex",gap:8,marginTop:6}}>
                <VajraBtn variant="outline" small onClick={saveSession} type="button">{editSessionIdx!==null?"✓ 保存修改":"+ 添加场次"}</VajraBtn>
                {editSessionIdx!==null&&<VajraBtn variant="outline" small onClick={()=>{setEditSessionIdx(null);setEvSession({date:"",time:"",title_zh:"",price:0,free:false});}} type="button">取消</VajraBtn>}
              </div>
            </div>
            {/* Flyers */}
            <div style={{marginBottom:10}}>
              <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:5}}>中文海报 FLYER ZH</label>
              <ImageUpload currentUrl={evForm.flyer_zh} onUpload={(url)=>setEvForm({...evForm,flyer_zh:url})} label="上传中文海报"/>
              {evForm.flyer_zh && (
                <input value={evForm.flyer_zh} onChange={e=>setEvForm({...evForm,flyer_zh:e.target.value})}
                  placeholder="或直接输入URL" style={{width:"100%",padding:"6px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:11,color:"#888",boxSizing:"border-box",marginTop:6,marginBottom:12}}/>
              )}
              <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:5}}>英文海报 FLYER EN</label>
              <ImageUpload currentUrl={evForm.flyer_en} onUpload={(url)=>setEvForm({...evForm,flyer_en:url})} label="上传英文海报"/>
              {evForm.flyer_en && (
                <input value={evForm.flyer_en} onChange={e=>setEvForm({...evForm,flyer_en:e.target.value})}
                  placeholder="或直接输入URL" style={{width:"100%",padding:"6px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:11,color:"#888",boxSizing:"border-box",marginTop:6}}/>
              )}
            </div>
            <div style={{marginBottom:10}}>
              <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:4}}>周期规则 RECURRING RULE</label>
              <input value={evForm.recurring_rule} onChange={e=>setEvForm({...evForm,recurring_rule:e.target.value})}
                placeholder="如：每周六 10:00 AM EST（留空=单次）"
                style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)"}}/>
            </div>
            <div style={{marginBottom:10}}>
              <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:4}}>传承标签 SPIRITUAL GRANT KEY</label>
              <input value={evForm.spiritual_grant} onChange={e=>setEvForm({...evForm,spiritual_grant:e.target.value})}
                placeholder="如：dzogchen / vajrakilaya（出席后自动标注）"
                style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)"}}/>
            </div>
            <div style={{marginBottom:10}}>
              <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:4}}>随喜类型 DONATION TYPE</label>
              <select value={evForm.donation_type} onChange={e=>setEvForm({...evForm,donation_type:e.target.value})}
                style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)"}}>
                <option value="free">免费 Free</option>
                <option value="donation">随喜 Donation</option>
                <option value="fixed">固定费用 Fixed Fee</option>
              </select>
            </div>
            {evForm.donation_type === "fixed" && (
              <div style={{marginBottom:10}}>
                <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:4}}>金额（加元）FEE AMOUNT (CAD $)</label>
                <input type="number" min="0" step="1" value={evForm.fee_amount} onChange={e=>setEvForm({...evForm,fee_amount:e.target.value})}
                  placeholder="如：300 = $300 CAD"
                  style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)"}}/>
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <div>
                <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:4}}>主法上师 TEACHER</label>
                <select value={evForm.teacher_id||""} onChange={e=>setEvForm({...evForm,teacher_id:e.target.value?parseInt(e.target.value):null})}
                  style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)"}}>
                  <option value="">-- 选择上师（选填）--</option>
                  {teacherList.map(t=><option key={t.id} value={t.id}>{t.name_zh}{t.name_en?` / ${t.name_en}`:""}</option>)}
                </select>
              </div>
              <div>
                <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:4}}>关联法教 TEACHING</label>
                <select value={evForm.teaching_id||""} onChange={e=>setEvForm({...evForm,teaching_id:e.target.value?parseInt(e.target.value):null})}
                  style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)"}}>
                  <option value="">-- 选择法教（选填）--</option>
                  {teachingList.map(t=><option key={t.id} value={t.id}>{t.title_zh}</option>)}
                </select>
              </div>
            </div>
            <div style={{marginBottom:10}}>
              <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:4}}>关联寺院 TEMPLE</label>
              <select value={evForm.temple_id||""} onChange={e=>setEvForm({...evForm,temple_id:e.target.value?parseInt(e.target.value):null})}
                style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)"}}>
                <option value="">-- 关联寺院（选填）--</option>
                {templeList.map(t=><option key={t.id} value={t.id}>{t.name_zh}{t.location?` · ${t.location}`:""}</option>)}
              </select>
            </div>
            <div style={{marginBottom:10}}>
              <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:4}}>关联专题 TOPIC HUB</label>
              <select value={evForm.topic_hub_slug||""} onChange={e=>setEvForm({...evForm,topic_hub_slug:e.target.value||null})}
                style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)"}}>
                <option value="">— 无关联专题 —</option>
                {hubList.map(h=><option key={h.slug} value={h.slug}>{h.title_zh} ({h.slug})</option>)}
              </select>
            </div>
            <div style={{marginBottom:10}}>
              <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:4}}>能量方向 ENERGY DIRECTION（烦恼匹配）</label>
              <select value={evForm.energy_direction||""} onChange={e=>setEvForm({...evForm,energy_direction:e.target.value||""})}
                style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)"}}>
                <option value="">— 无（不参与匹配）—</option>
                <option value="tara">🕊 tara — 情感滋养（度母共修）</option>
                <option value="wealth">💰 wealth — 财富流通（财神法会）</option>
                <option value="health">🌿 health — 身体调和（藏医宇妥宁提）</option>
                <option value="obstacle">🔥 obstacle — 净障除碍（普巴金刚）</option>
                <option value="anxiety">🍃 anxiety — 内心的安静（禅修/度母）</option>
                <option value="general">✨ general — 通用</option>
              </select>
            </div>
            <div style={{marginBottom:10}}>
              <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:5}}>封面图片 COVER IMAGE</label>
              <ImageUpload currentUrl={evForm.cover_image_url} onUpload={(url)=>setEvForm({...evForm,cover_image_url:url})} label="上传封面图"/>
              {evForm.cover_image_url && (
                <input value={evForm.cover_image_url} onChange={e=>setEvForm({...evForm,cover_image_url:e.target.value})}
                  placeholder="或直接输入URL" style={{width:"100%",padding:"6px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:11,color:"#888",boxSizing:"border-box",marginTop:6}}/>
              )}
            </div>
            <div style={{marginBottom:10}}>
              <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:4}}>照片墙 GALLERY IMAGES (每行一个URL)</label>
              <textarea value={evForm.gallery_images} onChange={e=>setEvForm({...evForm,gallery_images:e.target.value})}
                placeholder={"https://i.imgur.com/xxx.jpg\nhttps://i.imgur.com/yyy.jpg"}
                rows={4} style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)",resize:"vertical",fontFamily:"monospace"}}/>
            </div>
            <div style={{fontSize:11,color:"rgba(0,0,0,0.45)",opacity:.8,marginBottom:8}}>
              ✅ intro_short / intro_paragraphs 已废弃 — 请使用 description_zh / description_en
            </div>
            <div style={{marginBottom:10}}>
              <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:4}}>中文描述 DESCRIPTION ZH</label>
              <textarea value={evForm.description_zh} onChange={e=>setEvForm({...evForm,description_zh:e.target.value})}
                placeholder="活动中文介绍（显示于Carousel和活动详情）" rows={3}
                style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)",resize:"vertical"}}/>
            </div>
            <div style={{marginBottom:10}}>
              <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:4}}>English Description DESCRIPTION EN</label>
              <textarea value={evForm.description_en} onChange={e=>setEvForm({...evForm,description_en:e.target.value})}
                placeholder="Event English description (shown in Carousel and event detail)" rows={3}
                style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)",resize:"vertical"}}/>
            </div>
            <div style={{marginBottom:10}}>
              <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:4}}>参加须知 REQUIREMENTS (JSON ["须知1","须知2"])</label>
              <textarea value={evForm.requirements} onChange={e=>setEvForm({...evForm,requirements:e.target.value})}
                placeholder='["具备基础皈依发心","能持守闭关期间纪律"]' rows={3}
                style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)",resize:"vertical"}}/>
            </div>
            <div style={{marginBottom:10}}>
              <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:4}}>费用说明 FEE NOTE</label>
              <input value={evForm.fee_note} onChange={e=>setEvForm({...evForm,fee_note:e.target.value})}
                placeholder="报名费请通过 E-transfer 至..."
                style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)"}}/>
            </div>
            <div style={{display:"flex",gap:20,marginBottom:14}}>
              <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:"#555",cursor:"pointer"}}>
                <input type="checkbox" checked={evForm.is_external} onChange={e=>setEvForm({...evForm,is_external:e.target.checked})}/>
                外部活动（仅展示，无报名）
              </label>
              <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:"#555",cursor:"pointer"}}>
                <input type="checkbox" checked={evForm.is_featured} onChange={e=>setEvForm({...evForm,is_featured:e.target.checked})}/>
                主推推荐
              </label>
            </div>
            <VajraBtn variant="green" type="submit">{editingEventId?"保存修改":"创建法会"}</VajraBtn>
          </form>

          {/* Event list */}
          {eventList.length > 0 && (
            <div style={{marginTop:24}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <div style={{fontSize:12,color:"#aaa",letterSpacing:1.2,textTransform:"uppercase"}}>已有活动 ({showAll?eventList.length:eventList.filter(e=>e.is_active!==false).length}{showAll?" 全部":""})</div>
                <button onClick={()=>setShowAll(s=>!s)}
                  style={{fontSize:10,padding:"3px 10px",borderRadius:10,cursor:"pointer",
                    background:showAll?"rgba(212,175,55,0.12)":"transparent",
                    border:`1px solid ${showAll?GD:"rgba(0,0,0,0.15)"}`,color:showAll?GD:"#999"}}>
                  {showAll?"仅活跃":"显示全部"}
                </button>
              </div>
              {/* Category filter tabs */}
              <div style={{display:"flex",gap:0,marginBottom:12,borderBottom:"1px solid rgba(0,0,0,0.08)"}}>
                {[
                  {k:"",l:"全部"},
                  {k:"teaching",l:"教法"},
                  {k:"group_practice",l:"共修"},
                  {k:"important",l:"重要"},
                  {k:"feature",l:"精选"},
                ].map(t=>(
                  <button key={t.k} onClick={()=>setCatFilter(t.k)} style={{
                    padding:"5px 14px",fontSize:11,cursor:"pointer",background:"transparent",border:"none",
                    borderBottom:`2px solid ${catFilter===t.k?"#D4AF37":"transparent"}`,
                    color:catFilter===t.k?"#6B4F10":"#999",transition:"all .15s",
                  }}>{t.l}</button>
                ))}
              </div>
              {(showAll?eventList:eventList.filter(e=>e.is_active!==false))
                .filter(ev => !catFilter || ev.event_category === catFilter || ev.event_type === catFilter)
                .map(ev=>(
                <div key={ev.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 12px",borderRadius:8,background:"rgba(0,0,0,0.02)",border:"1px solid rgba(0,0,0,0.06)",marginBottom:6}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:ev.is_active===false?"#ccc":"#333"}}>
                      {ev.title_zh}
                      {ev.is_active===false&&<span style={{fontSize:10,color:"#e57373",marginLeft:8,border:"1px solid #e57373",borderRadius:6,padding:"1px 6px"}}>已停用</span>}
                      {ev.recurring_rule&&<span style={{fontSize:10,color:"#1e3a5f",background:"rgba(30,58,95,0.08)",border:"1px solid rgba(30,58,95,0.2)",borderRadius:8,padding:"1px 7px",marginLeft:6}}>🔁 {ev.recurring_rule}</span>}
                    </div>
                    <div style={{fontSize:11,color:"#999"}}>{ev.event_type||"teaching"} · {ev.start_date||"日期待定"} {ev.is_public?"· 公开":"· 内部"}</div>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <VajraBtn small variant="outline" onClick={()=>{setRegModal({event_id:ev.id,title:ev.title_zh});loadRegs(ev.id);}} style={{color:"#059669",borderColor:"#059669"}}>💰 报名</VajraBtn>
                    <VajraBtn small variant="outline" onClick={()=>startEditEvent(ev)}>编辑</VajraBtn>
                    {ev.is_active!==false
                      ? <VajraBtn small variant="outline" onClick={()=>deleteEvent(ev.id)} style={{color:"#e57373",borderColor:"#e57373"}}>停用</VajraBtn>
                      : <VajraBtn small variant="outline" onClick={()=>reactivateEvent(ev.id)} style={{color:"#059669",borderColor:"#059669"}}>启用</VajraBtn>}
                    <VajraBtn small variant="outline" onClick={()=>permanentDeleteEvent(ev.id)} style={{color:"#999",borderColor:"#ccc"}}>删除</VajraBtn>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* SERIES MODE                                                          */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      {viewMode === 'series' && (
        <div>
          {seriesMsg && (
            <div style={{fontSize:13,padding:"8px 14px",borderRadius:8,marginBottom:14,
              background:seriesMsg.startsWith("✓")?"rgba(6,78,59,0.06)":"rgba(107,26,26,0.06)",
              color:seriesMsg.startsWith("✓")?DG:CR}}>{seriesMsg}</div>
          )}

          {/* ── Series List (no series selected) ────────────────────────── */}
          {!activeSeries && (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={{fontSize:13,color:DG,fontWeight:600}}>法会系列列表</div>
                <VajraBtn small variant="outline" onClick={loadSeriesList}>刷新</VajraBtn>
              </div>
              {seriesLoading && <div style={{fontSize:13,color:"#999",padding:"20px 0",textAlign:"center"}}>加载中…</div>}
              {!seriesLoading && seriesList.length === 0 && (
                <div style={{fontSize:13,color:"#999",padding:"28px 0",textAlign:"center",border:"1px dashed rgba(0,0,0,0.1)",borderRadius:8}}>
                  暂无系列 · 请通过上方「📸 从海报创建法会」新建系列法会
                </div>
              )}
              {seriesList.map(s => (
                <div key={s.id} onClick={()=>loadSeries(s.id)}
                  style={{padding:"14px 16px",borderRadius:8,marginBottom:8,cursor:"pointer",
                    background:"#FFFDF5",border:`1px solid rgba(212,175,55,0.25)`,
                    transition:"border .15s, background .15s",
                  }}
                  onMouseEnter={e=>{e.currentTarget.style.border=`1px solid ${GD}`;e.currentTarget.style.background="#FEF9EC";}}
                  onMouseLeave={e=>{e.currentTarget.style.border="1px solid rgba(212,175,55,0.25)";e.currentTarget.style.background="#FFFDF5";}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:14,fontWeight:600,color:DG}}>{s.title_zh}</div>
                      {s.title_en && <div style={{fontSize:11,color:"#888",marginTop:1}}>{s.title_en}</div>}
                      <div style={{fontSize:12,color:"#999",marginTop:4}}>
                        {s.teacher_zh && <span>{s.teacher_zh} · </span>}
                        {s.start_date}{s.end_date && s.end_date !== s.start_date ? ` — ${s.end_date}` : ""}
                      </div>
                    </div>
                    <span style={{
                      fontSize:10,padding:"2px 8px",borderRadius:10,fontWeight:600,
                      background:s.is_public?"rgba(6,78,59,0.1)":"rgba(0,0,0,0.05)",
                      color:s.is_public?DG:"#999",
                    }}>{s.is_public ? "已发布" : "草稿"}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Series Editor (series selected) ─────────────────────────── */}
          {activeSeries && (
            <div>
              {/* Breadcrumb */}
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
                <button onClick={()=>{setActiveSeries(null);setSeriesEvents([]);setEditingSeriesEvId(null);if(!seriesList.length)loadSeriesList();}}
                  style={{background:"none",border:`1px solid rgba(212,175,55,0.4)`,borderRadius:6,padding:"4px 12px",fontSize:12,color:GD,cursor:"pointer",fontWeight:600}}>
                  ← 返回列表
                </button>
                <span style={{fontSize:14,fontWeight:600,color:DG,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {activeSeries.title_zh}
                </span>
                {activeSeries.is_public && (
                  <span style={{fontSize:10,color:DG,border:`1px solid ${DG}`,borderRadius:8,padding:"2px 8px",fontWeight:600}}>已发布</span>
                )}
              </div>

              {/* Series Metadata Card */}
              <div style={{border:`1px solid rgba(212,175,55,0.5)`,borderRadius:10,padding:"16px 18px",background:"#FFFDF5",marginBottom:16}}>
                <div style={{fontSize:11,fontWeight:700,color:GD,letterSpacing:1.5,textTransform:"uppercase",marginBottom:12}}>系列信息</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  <VajraInput label="系列标题（中文）*" value={seriesForm.title_zh} onChange={v=>setSeriesForm({...seriesForm,title_zh:v})}/>
                  <VajraInput label="Series Title (English)" value={seriesForm.title_en} onChange={v=>setSeriesForm({...seriesForm,title_en:v})}/>
                  <VajraInput label="上师（中文）" value={seriesForm.teacher_zh} onChange={v=>setSeriesForm({...seriesForm,teacher_zh:v})}/>
                  <VajraInput label="Teacher (English)" value={seriesForm.teacher_en} onChange={v=>setSeriesForm({...seriesForm,teacher_en:v})}/>
                  <VajraInput label="开始日期" value={seriesForm.start_date} onChange={v=>setSeriesForm({...seriesForm,start_date:v})} type="date" small/>
                  <VajraInput label="结束日期" value={seriesForm.end_date} onChange={v=>setSeriesForm({...seriesForm,end_date:v})} type="date" small/>
                </div>
                <VajraInput label="系列描述（选填）" value={seriesForm.description} onChange={v=>setSeriesForm({...seriesForm,description:v})} placeholder="系列简介"/>
                <div style={{marginBottom:8}}>
                  <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:4}}>中文描述 DESCRIPTION ZH</label>
                  <textarea value={seriesForm.description_zh} onChange={e=>setSeriesForm({...seriesForm,description_zh:e.target.value})}
                    placeholder="系列中文介绍（显示于Carousel和系列页）" rows={3}
                    style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)",resize:"vertical"}}/>
                </div>
                <div style={{marginBottom:8}}>
                  <label style={{fontSize:11,color:"rgba(184,134,11,0.7)",letterSpacing:1,display:"block",marginBottom:4}}>English Description DESCRIPTION EN</label>
                  <textarea value={seriesForm.description_en} onChange={e=>setSeriesForm({...seriesForm,description_en:e.target.value})}
                    placeholder="Series English description" rows={3}
                    style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)",resize:"vertical"}}/>
                </div>
                <div style={{marginBottom:8}}>
                  <label style={{display:"block",fontSize:11,color:"#aaa",letterSpacing:1.2,textTransform:"uppercase",marginBottom:5}}>主题色</label>
                  <select value={seriesForm.theme_color||"indigo-gold"} onChange={e=>setSeriesForm({...seriesForm,theme_color:e.target.value})}
                    style={{width:"100%",padding:"7px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13}}>
                    <option value="indigo-gold">深蓝+金 (默认)</option>
                    <option value="red-gold">红+金</option>
                    <option value="sakya">白蓝红 (萨迦)</option>
                  </select>
                </div>
                <VajraInput label="页面 Slug（选填）" value={seriesForm.slug||""} onChange={v=>setSeriesForm({...seriesForm,slug:v})} placeholder="如：thamthog-2026（用于 /vajra/special/xxx）" small/>
                <VajraInput label="Flyer URL" value={seriesForm.flyer_url} onChange={v=>setSeriesForm({...seriesForm,flyer_url:v})} placeholder="https://..."/>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  <VajraInput label="Website" value={seriesForm.website} onChange={v=>setSeriesForm({...seriesForm,website:v})} placeholder="https://..."/>
                  <VajraInput label="联系方式 Contact" value={seriesForm.contact} onChange={v=>setSeriesForm({...seriesForm,contact:v})} placeholder="邮箱或电话"/>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:16,marginTop:10}}>
                  <label style={{display:"flex",alignItems:"center",gap:6,fontSize:13,color:"#555",cursor:"pointer"}}>
                    <input type="checkbox" checked={seriesForm.is_public} onChange={e=>setSeriesForm({...seriesForm,is_public:e.target.checked})}/>
                    公开发布整个系列
                  </label>
                  <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"#d4a84b",cursor:"pointer",fontWeight:600}}>
                    <input type="checkbox" checked={seriesForm.is_featured||false} onChange={e=>setSeriesForm({...seriesForm,is_featured:e.target.checked})}/>
                    ⭐ 特别介绍（生成专属页面 + 首页横幅）
                  </label>
                  {seriesForm.slug && <a href={`/vajra/special/${seriesForm.slug}`} target="_blank" style={{fontSize:11,color:GD}}>预览页面 →</a>}
                  <VajraBtn variant="green" small onClick={saveSeriesMetadata}>保存系列信息</VajraBtn>
                </div>
              </div>

              {/* Events Table */}
              <div style={{border:`1px solid rgba(212,175,55,0.3)`,borderRadius:10,overflow:"hidden",marginBottom:16}}>
                <div style={{padding:"10px 16px",background:"rgba(212,175,55,0.08)",borderBottom:"1px solid rgba(212,175,55,0.2)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:12,fontWeight:700,color:GD,letterSpacing:1,textTransform:"uppercase"}}>
                    场次列表（{seriesEvents.length}）
                  </span>
                  <VajraBtn variant="outline" small onClick={addSeriesEventFn}>+ 新增场次</VajraBtn>
                </div>

                {seriesLoading && <div style={{fontSize:13,color:"#999",padding:"20px 16px",textAlign:"center"}}>加载中…</div>}
                {!seriesLoading && seriesEvents.length === 0 && (
                  <div style={{fontSize:13,color:"#999",padding:"24px 16px",textAlign:"center"}}>
                    暂无场次 · 点击上方「+ 新增场次」添加
                  </div>
                )}

                {seriesEvents.map((ev, i) => (
                  <React.Fragment key={ev.id}>
                    {/* Row */}
                    <div style={{
                      display:"flex",alignItems:"center",padding:"10px 16px",
                      background:editingSeriesEvId===ev.id?"#FEF9EC":"transparent",
                      borderBottom:editingSeriesEvId===ev.id?"none":"1px solid rgba(0,0,0,0.05)",
                      borderLeft:editingSeriesEvId===ev.id?`3px solid ${GD}`:"3px solid transparent",
                    }}>
                      <span style={{fontSize:11,color:"#bbb",minWidth:22,textAlign:"center",marginRight:8}}>{i+1}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,color:"rgba(0,0,0,0.82)",fontWeight:editingSeriesEvId===ev.id?600:400,
                          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {ev.title_zh}
                        </div>
                        <div style={{fontSize:11,color:"#999",marginTop:1}}>
                          {ev.start_date}{ev.end_date&&ev.end_date!==ev.start_date?` — ${ev.end_date}`:""}{ev.start_time?` · ${ev.start_time}`:""}
                          {ev.event_type&&<span style={{marginLeft:6,background:"rgba(0,0,0,0.05)",borderRadius:4,padding:"0 5px",fontSize:10}}>{ev.event_type}</span>}
                          {ev.is_public&&<span style={{marginLeft:6,color:DG,fontSize:10}}>公开</span>}
                        </div>
                      </div>
                      <div style={{display:"flex",gap:6,marginLeft:8}}>
                        <VajraBtn small variant="outline" onClick={()=>editingSeriesEvId===ev.id?setEditingSeriesEvId(null):startEditSeriesEvent(ev)}>
                          {editingSeriesEvId===ev.id?"收起":"编辑"}
                        </VajraBtn>
                        <VajraBtn small variant="outline" onClick={()=>deleteSeriesEventFn(ev.id)} style={{color:"#e57373",borderColor:"#e57373"}}>删</VajraBtn>
                      </div>
                    </div>

                    {/* Inline Event Edit Panel */}
                    {editingSeriesEvId === ev.id && (
                      <div style={{margin:"0 12px 12px",padding:"16px 14px",background:"#FDFBF7",borderRadius:"0 0 8px 8px",border:`1px solid rgba(212,175,55,0.3)`,borderTop:"none"}}>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                          <VajraInput label="标题（中文）*" value={seriesEvForm.title_zh} onChange={v=>setSeriesEvForm({...seriesEvForm,title_zh:v})}/>
                          <VajraInput label="Title (English)" value={seriesEvForm.title_en} onChange={v=>setSeriesEvForm({...seriesEvForm,title_en:v})}/>
                          <VajraInput label="上师（中文）" value={seriesEvForm.teacher_zh} onChange={v=>setSeriesEvForm({...seriesEvForm,teacher_zh:v})}/>
                          <VajraInput label="Teacher (English)" value={seriesEvForm.teacher_en} onChange={v=>setSeriesEvForm({...seriesEvForm,teacher_en:v})}/>
                        </div>
                        <VajraInput label="地点" value={seriesEvForm.location} onChange={v=>setSeriesEvForm({...seriesEvForm,location:v})} placeholder="地址 / Zoom（线上）"/>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
                          <div>
                            <div style={{fontSize:11,color:"#aaa",letterSpacing:1.2,textTransform:"uppercase",marginBottom:6}}>活动类型</div>
                            <select value={seriesEvForm.event_type} onChange={e=>setSeriesEvForm({...seriesEvForm,event_type:e.target.value})}
                              style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)"}}>
                              <option value="weekly_practice">周共修</option>
                              <option value="teaching">开示</option>
                              <option value="empowerment">灌顶</option>
                              <option value="retreat">禅修营</option>
                              <option value="puja">法会</option>
                              <option value="ritual">法事</option>
                              <option value="other">其他</option>
                            </select>
                          </div>
                          <div>
                            <div style={{fontSize:11,color:"#aaa",letterSpacing:1.2,textTransform:"uppercase",marginBottom:6}}>随喜类型</div>
                            <select value={seriesEvForm.donation_type} onChange={e=>setSeriesEvForm({...seriesEvForm,donation_type:e.target.value})}
                              style={{width:"100%",padding:"8px 10px",border:"1px solid rgba(0,0,0,0.12)",borderRadius:7,fontSize:13,background:"rgba(255,255,255,0.9)"}}>
                              <option value="free">免费</option>
                              <option value="donation">随喜</option>
                              <option value="fixed">固定费用</option>
                            </select>
                          </div>
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                          <VajraInput label="开始日期" value={seriesEvForm.start_date} onChange={v=>setSeriesEvForm({...seriesEvForm,start_date:v})} type="date" small/>
                          <VajraInput label="结束日期" value={seriesEvForm.end_date} onChange={v=>setSeriesEvForm({...seriesEvForm,end_date:v})} type="date" small/>
                          <VajraInput label="开始时间" value={seriesEvForm.start_time} onChange={v=>setSeriesEvForm({...seriesEvForm,start_time:v})} type="time" small/>
                          <VajraInput label="结束时间" value={seriesEvForm.end_time} onChange={v=>setSeriesEvForm({...seriesEvForm,end_time:v})} type="time" small/>
                        </div>
                        {seriesEvForm.donation_type === "fixed" && (
                          <VajraInput label="金额（CAD $）" value={seriesEvForm.fee_amount} onChange={v=>setSeriesEvForm({...seriesEvForm,fee_amount:v})} placeholder="如：150"/>
                        )}
                        <div style={{fontSize:11,color:"rgba(0,0,0,0.45)",opacity:.8,marginBottom:8}}>✅ intro_short 已废弃 — 请使用 description_zh / description_en</div>
                        <VajraInput label="费用说明（选填）" value={seriesEvForm.fee_note} onChange={v=>setSeriesEvForm({...seriesEvForm,fee_note:v})} placeholder="报名费方式"/>
                        {(seriesEvForm.event_type==="puja"||seriesEvForm.event_type==="ritual"||seriesEvForm.event_type==="empowerment") && (
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
                            <div>
                              <div style={{fontSize:11,color:"#aaa",letterSpacing:1.2,textTransform:"uppercase",marginBottom:6}}>法会功能</div>
                              <select value={seriesEvForm.spiritual_function} onChange={e=>setSeriesEvForm({...seriesEvForm,spiritual_function:e.target.value})}
                                style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e0d0",borderRadius:8,fontSize:13,background:"#fafafa"}}>
                                <option value="">-- 选择功能 --</option>
                                <option value="息灾">息灾</option><option value="增福">增福</option><option value="降伏">降伏</option>
                                <option value="敬爱">敬爱</option><option value="长寿">长寿</option><option value="净化">净化</option>
                              </select>
                            </div>
                            <div>
                              <div style={{fontSize:11,color:"#aaa",letterSpacing:1.2,textTransform:"uppercase",marginBottom:6}}>五行亲和</div>
                              <select value={seriesEvForm.element_affinity} onChange={e=>setSeriesEvForm({...seriesEvForm,element_affinity:e.target.value})}
                                style={{width:"100%",padding:"8px 10px",border:"1px solid #e5e0d0",borderRadius:8,fontSize:13,background:"#fafafa"}}>
                                <option value="">-- 选择 --</option>
                                <option value="Water">水</option><option value="Fire">火</option><option value="Wood">木</option>
                                <option value="Metal">金</option><option value="Earth">土</option>
                              </select>
                            </div>
                          </div>
                        )}
                        <div style={{display:"flex",gap:16,marginBottom:12}}>
                          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:13,color:"#555",cursor:"pointer"}}>
                            <input type="checkbox" checked={seriesEvForm.is_public} onChange={e=>setSeriesEvForm({...seriesEvForm,is_public:e.target.checked})}/>
                            公开显示
                          </label>
                          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:13,color:"#555",cursor:"pointer"}}>
                            <input type="checkbox" checked={seriesEvForm.registration_required} onChange={e=>setSeriesEvForm({...seriesEvForm,registration_required:e.target.checked})}/>
                            需要报名
                          </label>
                        </div>
                        <div style={{display:"flex",gap:8}}>
                          <VajraBtn variant="green" small onClick={()=>saveSeriesEventFn(ev.id)}>保存此场次</VajraBtn>
                          <VajraBtn variant="outline" small onClick={()=>setEditingSeriesEvId(null)}>取消</VajraBtn>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Delete Series */}
              <div style={{display:"flex",justifyContent:"flex-end",marginTop:4}}>
                <VajraBtn variant="outline" small onClick={deleteSeriesFn} style={{color:"#e57373",borderColor:"#e57373"}}>
                  🗑 删除整个系列（含所有场次）
                </VajraBtn>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Registration Modal ── */}
      {regModal && (
        <div onClick={()=>{setRegModal(null);setRegList([]);setRegMsg("");}} style={{
          position:"fixed",inset:0,zIndex:3000,background:"rgba(0,0,0,0.5)",
          display:"flex",alignItems:"center",justifyContent:"center",
        }}>
          <div onClick={e=>e.stopPropagation()} style={{
            background:"#fff",borderRadius:12,padding:"24px",
            width:"min(680px,92vw)",maxHeight:"80vh",overflow:"auto",
            boxShadow:"0 8px 32px rgba(0,0,0,0.2)",
          }}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div style={{fontSize:14,fontWeight:600,color:"#333"}}>💰 报名管理 · {regModal.title}</div>
              <button onClick={()=>{setRegModal(null);setRegList([]);setRegMsg("");}} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"#999"}}>✕</button>
            </div>

            {financeData && (
              <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
                {[
                  {label:"报名总数", val:financeData.registrations_total, color:"#333"},
                  {label:"已确认", val:financeData.payment_confirmed, color:"#059669"},
                  {label:"待确认", val:financeData.payment_pending, color:"#d97706"},
                  {label:"已减免", val:financeData.payment_waived, color:"#6b7280"},
                  {label:"已收金额", val:`$${(financeData.total_confirmed_amount||0).toFixed(2)}`, color:"#1d4ed8"},
                ].map(item=>(
                  <div key={item.label} style={{textAlign:"center",padding:"8px 16px",borderRadius:8,background:"rgba(0,0,0,0.04)"}}>
                    <div style={{fontSize:18,fontWeight:700,color:item.color}}>{item.val}</div>
                    <div style={{fontSize:10,color:"#999",letterSpacing:.5}}>{item.label}</div>
                  </div>
                ))}
              </div>
            )}

            {regMsg && <div style={{fontSize:12,padding:"6px 10px",borderRadius:6,marginBottom:10,
              background:regMsg.startsWith("✓")?"rgba(6,78,59,0.08)":"rgba(107,26,26,0.08)",
              color:regMsg.startsWith("✓")?"#059669":"#dc2626"}}>{regMsg}</div>}

            {regLoading && <div style={{textAlign:"center",padding:"20px",color:"#999"}}>加载中…</div>}

            {!regLoading && regList.length === 0 && (
              <div style={{textAlign:"center",padding:"20px",color:"#ccc",fontSize:13}}>暂无报名记录</div>
            )}

            {!regLoading && regList.map(reg => {
              const ps = reg.payment_status || "pending";
              const statusColor = {confirmed:"#059669",pending:"#d97706",waived:"#6b7280"}[ps] || "#999";
              return (
                <div key={reg.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                  padding:"10px 12px",borderRadius:8,border:"1px solid rgba(0,0,0,0.07)",marginBottom:6}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600,color:"#333"}}>{reg.name}</div>
                    <div style={{fontSize:11,color:"#999"}}>{reg.email} {reg.pass_type?`· ${reg.pass_type}`:""}</div>
                    <div style={{fontSize:11,marginTop:2}}>
                      <span style={{color:statusColor,fontWeight:500}}>
                        ●  {ps==="confirmed"?"已付款":ps==="waived"?"已减免":"待付款"}
                      </span>
                      {reg.payment_amount && <span style={{color:"#6b7280",marginLeft:6}}>${reg.payment_amount}</span>}
                      {reg.payment_method && <span style={{color:"#6b7280",marginLeft:6}}>· {reg.payment_method}</span>}
                    </div>
                  </div>
                  <div style={{display:"flex",gap:6,flexShrink:0}}>
                    {ps !== "confirmed" && (
                      <button onClick={()=>markPayment(reg.id,"confirmed")} style={{
                        fontSize:10,padding:"3px 10px",borderRadius:6,cursor:"pointer",
                        background:"rgba(5,150,105,0.08)",color:"#059669",border:"1px solid rgba(5,150,105,0.25)"}}>
                        ✓ 确认付款
                      </button>
                    )}
                    {ps !== "pending" && (
                      <button onClick={()=>markPayment(reg.id,"pending")} style={{
                        fontSize:10,padding:"3px 8px",borderRadius:6,cursor:"pointer",
                        background:"rgba(0,0,0,0.03)",color:"#999",border:"1px solid #e5e7eb"}}>
                        重置
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
